/**
 * The signed-in setup (ensureUserProfile in AuthProvider) runs once per user
 * per page load, and the lead-gen invite safety net asks the server once per
 * tab session.
 *
 * Walk of 05 Oct 26 (item 9): every full load of /app and /app/l/<id>/* ran
 * the setup two or three times (getSession, then the SIGNED_IN and
 * INITIAL_SESSION events each started it), so 21 loads made 52 invite RPC
 * calls. The setup's work (create a missing profile, accept a pending lead-gen
 * invite, create a default workspace, start a trial) is the same every time it
 * runs on one load, so the later starts share the first one's promise.
 *
 * The invite question (get_pending_lead_gen_invite_token_for_self) is
 * remembered for the tab once it answers "no invite". It is asked again when
 * the tab opens the invite page (forgetInviteChecksOnInvitePage) or the
 * invite page left its token behind, which is the path the safety net exists
 * for: the OAuth round trip from that page landing on /fly-app instead.
 */

export const LEAD_GEN_INVITE_PAGE = '/accept-leads-generator-invitation';
const NO_INVITE_PREFIX = 'fly:no-lead-gen-invite:';
/** Set by AcceptLeadsGeneratorInvitation on mount (see profileSetup.ts ensureDefaultWorkspace). */
const PENDING_INVITE_TOKEN_KEY = 'pending_lead_gen_invite_token';

const running = new Map<string, Promise<boolean>>();

/**
 * Runs `setup` for this user once on this page load; a second start gets the
 * first one's promise. `setup` resolves true when every step finished and
 * false when a step failed (the setup logs and swallows its own errors, so
 * the user is never blocked). A failed run is let go, so the next sign-in
 * event on this load starts it again, as every event did before.
 */
export function onceForUser(userId: string, setup: () => Promise<boolean>): Promise<boolean> {
  const held = running.get(userId);
  if (held) {
    console.log('[profileSetupOnce] setup already started on this load, sharing it for', userId);
    return held;
  }
  const letGo = () => {
    if (running.get(userId) === started) running.delete(userId);
  };
  const started: Promise<boolean> = setup().then(
    (done) => {
      if (!done) {
        console.warn('[profileSetupOnce] a setup step failed, the next sign-in event starts it again for', userId);
        letGo();
      }
      return done;
    },
    (err) => {
      console.warn('[profileSetupOnce] setup threw, the next sign-in event starts it again for', userId, err);
      letGo();
      throw err;
    },
  );
  running.set(userId, started);
  return started;
}

function session(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

/** True when this tab already heard "no pending invite" for this user, and nothing since says to ask again. */
export function noInviteKnown(userId: string): boolean {
  const s = session();
  if (!s) return false;
  try {
    if (s.getItem(PENDING_INVITE_TOKEN_KEY)) return false;
    return s.getItem(`${NO_INVITE_PREFIX}${userId}`) === '1';
  } catch {
    return false;
  }
}

/** Remembers, for this tab, that the server said this user has no pending invite. */
export function rememberNoInvite(userId: string): void {
  try {
    session()?.setItem(`${NO_INVITE_PREFIX}${userId}`, '1');
  } catch {
    // Storage blocked: the question is asked again on the next load, as before.
  }
}

/** On the invite page, every remembered "no invite" goes, so the next load asks the server again. */
export function forgetInviteChecksOnInvitePage(pathname: string): void {
  if (!pathname.startsWith(LEAD_GEN_INVITE_PAGE)) return;
  const s = session();
  if (!s) return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < s.length; i++) {
      const k = s.key(i);
      if (k && k.startsWith(NO_INVITE_PREFIX)) keys.push(k);
    }
    keys.forEach((k) => s.removeItem(k));
  } catch {
    // Storage blocked: nothing was remembered either.
  }
}

/** Tests only: a fresh page load. */
export function resetProfileSetupOnce(): void {
  running.clear();
}
